import { useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router';
import { queryClient } from '@/shared/api';
import { isNative, useNativeEvent } from '@/shared/bridge';
import {
  COMPACT_MAX_WIDTH,
  PullToRefreshIndicator,
  ScrollArea,
  SidebarInset,
  SidebarProvider,
  useCanHover,
  useIsCompact,
  usePullToRefresh,
  useSidebar,
} from '@/shared/ui';
import { AppBottomNav, AppHeader, AppSidebar, HeaderActionsProvider } from '@/widgets/app-nav';

/**
 * 라우트 전환 시 콘텐츠 스크롤을 최상단으로 리셋. 실제 스크롤러는 `window` 가 아니라 `AppShell` 의
 * `ScrollArea` **뷰포트**라 `window.scrollTo` 로는 안 된다. 셸(`ScrollArea`)은 라우트 사이에 리마운트되지
 * 않으므로 이전 페이지의 scrollTop 이 그대로 남는 것이 원인.
 *
 * heritage `ScrollArea` 는 뷰포트 ref 를 노출하지 않으므로, 스크롤 영역 **안**에 숨은 앵커를 두고
 * `closest('[data-slot=scroll-area-viewport]')` 로 자기 뷰포트를 찾아 리셋한다(전역 querySelector 로
 * 엉뚱한 ScrollArea 를 잡지 않게 self-scoped). **`pathname` 만** 의존 — 필터 등 search 파라미터 변경으로는
 * 스크롤을 건드리지 않는다.
 */
function ScrollResetOnRouteChange() {
  const { pathname } = useLocation();
  const anchorRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    anchorRef.current
      ?.closest<HTMLElement>('[data-slot=scroll-area-viewport]')
      ?.scrollTo({ top: 0 });
  }, [pathname]);
  return <span ref={anchorRef} aria-hidden className="hidden" />;
}

/**
 * 앱 본체 chrome — 인증 후 콘텐츠 화면(대시보드·발전통계·수익분석·이상알림·정보관리·고객센터) 공유 셸.
 *
 * 단일 셸(`AppShell`) — 좌측 `AppSidebar`(wide=icon-rail / compact=모바일 sheet, heritage 자동 전환) + `SidebarInset`
 * 안의 스크롤 콘텐츠. 화면 타이틀은 `AppHeader`(라우트 handle SSOT, iOS large-title).
 *
 * **전체 네비 진입점은 환경별로 하나씩** — wide=좌측 rail / compact+네이티브=하단 `AppBottomNav`(탭 바 +
 * "메뉴"=sheet 토글) / compact+브라우저=`AppHeader` 좌측 햄버거. 셋 다 같은 sheet(`AppSidebar`)를 연다.
 * scrollbar/하단 패딩만 분기 — 나머지 구조는 공용이라 브레이크포인트 넘어도 셸이 리마운트되지 않아
 * 스크롤·상태가 유지된다.
 * `SidebarProvider mobileBreakpoint={COMPACT_MAX_WIDTH}` 로 heritage sheet↔rail 경계를 compact 판정과 정합.
 * 콘텐츠 폭 = `max-w-app-content`(토큰 `--container-app-content`, tokens.css) — `AppHeader` 타이틀과 공유. 네비 정의·렌더는
 * `widgets/app-nav`(공용 config).
 */
export function AppLayout() {
  return (
    <SidebarProvider
      mobileBreakpoint={COMPACT_MAX_WIDTH}
      // 사이드바 폭은 토큰 SSOT — 콘텐츠 상한 3종(`--container-app-content*`)이 이 값에서 파생되므로 여기서
      // 하드코딩하면 둘이 어긋난다. prop 은 그대로 `--sidebar-width` 인라인 스타일로 들어가 var 참조가 유효.
      width="var(--heviton-sidebar-width)"
      widthMobile="18rem"
      className="h-dvh bg-heviton-bg-tinted"
      // 캔버스(html) 배경을 이 셸의 면 색으로 맞춘다 — iOS Safari 는 상태바·노치 주변을 **캔버스 배경
      // 전파**로 칠하므로, 안쪽 div 를 tinted 로 칠해도 그 영역만 `--background`(흰색)로 남아 노치 아래
      // 이음매가 생긴다. 실제 색 지정은 globals.css 의 `html:has([data-canvas='app'])`.
      data-canvas="app"
    >
      {/* AppSidebar 는 compact=sheet / wide=rail 로 양쪽 공용 → hoist(조건 무관 항상 렌더). */}
      <AppSidebar />
      <AppShell />
    </SidebarProvider>
  );
}

/**
 * 통합 셸 — `SidebarInset`(flex-1 `<main>`, variant=sidebar 라 inset 마진 비활성 → wide·compact 동일) 안에
 * 단일 `ScrollArea`. 네이티브 compact 전용: 하단 `AppBottomNav`(fixed) + 그만큼 하단 패딩 확보. 터치 기기는 scrollbar('scroll').
 * `useSidebar`/`useIsCompact` 는 provider 자식에서만 호출 가능해 AppLayout 이 아닌 본 컴포넌트가 소유.
 * min-w-0 — flex item 기본 min-width:auto 해제(넓은 콘텐츠가 inset 을 viewport 밖으로 안 밀게). min-h-0 — 스크롤 앵커.
 */
function AppShell() {
  const compact = useIsCompact();
  // 하단 탭 바는 **네이티브 앱 전용** — 브라우저 모바일은 `AppHeader` 좌측 햄버거가 전체 네비를 담당한다
  // (웹에서 하단 고정 바는 브라우저 UI·주소창과 겹쳐 손해가 크다). wide 는 좌측 rail 이 대체.
  const showBottomNav = compact && isNative();
  // scrollbar 표시 모드는 너비가 아니라 호버 가능 여부로 판단 — 태블릿 PC 는 wide(≥1024)여도 터치라
  // 'hover' 면 스크롤바가 영영 안 뜬다. 호버 가능(마우스/트랙패드)=상시 hover-reveal / 터치=스크롤 중 노출.
  const canHover = useCanHover();
  const { openMobile, setOpenMobile, state } = useSidebar();
  // 콘텐츠 상한 = 셸 총폭 − 현재 사이드바 폭. 사이드바가 내준 폭을 콘텐츠가 그대로 흡수해, 총폭보다 좁은
  // 화면(태블릿 가로 등)에서 우측 여백이 생기지 않는다. compact 우선 판정 — compact 에선 사이드바가 sheet
  // (레이아웃 점유 0)지만 `state` 는 wide 시절 토글값이 남아 있어 그대로 쓰면 한 단계 좁게 잡힌다.
  const contentWidthClass = compact
    ? 'max-w-app-content-mobile'
    : state === 'collapsed'
      ? 'max-w-app-content-collapsed'
      : 'max-w-app-content';
  // Pull-to-refresh — 앱 전역 단일 지점(이 ScrollArea 가 모든 페이지의 스크롤러). 갱신은 전체 무효화가 아니라
  // **활성 쿼리 refetch**(`type:'active'`) — 지금 화면에 붙은 것만 다시 받는다(비활성 캐시까지 때리면 낭비).
  // 호버 가능(마우스) 환경은 제스처 의미가 없어 비활성 — 터치 기기에서만 동작.
  const shellRef = useRef<HTMLDivElement>(null);
  const pullState = usePullToRefresh(
    shellRef,
    () => queryClient.refetchQueries({ type: 'active' }),
    !canHover,
  );
  // iOS 상태바 탭 → 최상단. 네이티브가 스크롤을 못 한다(실제 스크롤러가 이 `ScrollArea` 뷰포트, WKWebView 의
  // scrollView 가 아님) → native 는 제스처만 위임하고 여기서 수행. Android·데스크톱은 이벤트 자체가 없어 무해.
  const scrollToTop = useCallback(() => {
    shellRef.current
      ?.querySelector<HTMLElement>('[data-slot=scroll-area-viewport]')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  useNativeEvent('app.scrollToTop', scrollToTop);
  return (
    // 콘텐츠 면 = tint(회색). floating 사이드바(--sidebar=card, 흰색)보다 한 스텝 어두워야 패널이 떠 보인다.
    // `relative` = pull-to-refresh 인디케이터(absolute) 기준, `overflow-hidden` = 본문이 밀려 내려갈 때 하단
    // 넘침을 잘라 스크롤바가 생기지 않게.
    <SidebarInset
      className="relative min-w-0 min-h-0 overflow-hidden bg-heviton-bg-tinted"
      ref={shellRef}
    >
      {/* 인디케이터는 콘텐츠 **위**(z-20) — 아래 레이어에 두면 콘텐츠 배경에 가려 안 보인다. 클릭 통과
          (pointer-events-none)라 조작엔 영향 없음. */}
      <PullToRefreshIndicator {...pullState} />
      {/* 콘텐츠 레이어 — 당김 거리만큼 통째로 밀어내린다. 자체 배경(bg)을 칠해 스크롤된 본문이 상단 여백에
          비쳐 보이지 않게 한다. 드래그 중엔 transition off(손가락 1:1 추종), 놓는 순간 on 이라 복귀·임계
          고정이 부드럽게 스냅한다. */}
      <div
        className="relative z-10 h-full w-full bg-heviton-bg-tinted"
        style={{
          transform: `translateY(${pullState.offset}px)`,
          transition: pullState.dragging ? 'none' : 'transform 500ms cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* 공용 `ScrollArea` — overscroll 체이닝 차단(뷰포트 끝 바운스백)은 래퍼 기본. 스크롤바 인셋은 켜지 않는다:
            이 ScrollArea 가 `<Outlet/>` 을 품는 최상위라, 자손 선택자로 도는 인셋이 페이지 안 **모든** 중첩 스크롤
            영역(표·시트)의 스크롤바까지 물들였다(수익분석 표 상하 여백의 출처). 인셋이 필요하면 그 영역에서 켤 것. */}
        <ScrollArea
          className="w-full h-full min-h-0 transform-3d"
          scrollbarVisibility={canHover ? 'hover' : 'scroll'}
        >
          {/* 스크롤 영역 내부 앵커 — 라우트 전환 시 이 뷰포트를 top 으로 되돌린다. */}
          <ScrollResetOnRouteChange />
          {/* 하단 패딩 = safe-area + (하단 탭 노출 시 그 공간 6rem / 그 외 여백 2rem). */}
          {/* HeaderActionsProvider — AppHeader(슬롯 등록) + Outlet(페이지 포탈)을 함께 감싼다(a=헤더 trailing). */}
          <HeaderActionsProvider>
            <div
              className={`flex flex-1 flex-col p-4 lg:p-8 ${contentWidthClass} transition-[max-width] duration-200 ease-linear`}
              style={{
                paddingBottom: `calc(env(safe-area-inset-bottom) + ${showBottomNav ? '6rem' : '2rem'})`,
              }}
            >
              {/* AppHeader 를 콘텐츠와 같은 폭(`max-w-app-content`) 안에 — 헤더 trailing 액션 슬롯이 본문 우측 끝과 정렬(와이드). */}
              <div className="w-full">
                <AppHeader className="mb-4" />
                <Outlet />
              </div>
            </div>
          </HeaderActionsProvider>
        </ScrollArea>
      </div>
      {/* 하단 내비는 콘텐츠 레이어 **밖** — 당김에 같이 밀리면 안 된다(fixed 하단 고정 유지). */}
      {showBottomNav && (
        <AppBottomNav onOpenMenu={() => setOpenMobile(true)} menuOpen={openMobile} />
      )}
    </SidebarInset>
  );
}
